import { FC, useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faCheckCircle,
  faClock,
  faClockRotateLeft,
} from "@fortawesome/free-solid-svg-icons";
import { B2Form, B2InputGroup, B2Select } from "@hdb/biome-2";
import { FormControl } from "react-bootstrap";
import ModalComponent from "src/core/components/modal/modal.component";
import NewTableComponent from "src/core/components/table/new-table.component";
import SkeletonComponent from "src/shared/custom-spinner/skeleton.component";
import GetConfigTable from "src/core/components/table/get-config-table.component";
import "./configuration-history-table.scss";

type StatusType = "All" | "Success" | "Failed" | "Pending";

const PAGE_SIZE = 10;

export interface HistoryTableDetailRow {
  id: number;
  parameterName: string;
  previousValue: string;
  currentValue: string;
}

export interface ConfigurationHistoryTableProps {
  isOpen: boolean;
  handleClose: () => void;
  assetCode: string | null;
  // Display configuration
  title: string;
  subtitle?: string;
  retainedText: string;
  actionColumnTitle: string;
  actionTitleField: string;
  issueTimeField: string;
  acknowledgeTimeField: string;
  defaultSortBy?: string;
  // API
  queryKey: string;
  getHistory: (query: any) => Promise<any>;
  getHistoryDetail: (historyId: number, signal?: any) => Promise<any>;
  // Optional: refetch when a status changes (e.g. applying changes ACK)
  refetchTrigger?: number | "idle" | "pending" | "success" | "failed";
  // Optional: fallback data when the API is unavailable
  fallbackData?: any[];
  fallbackIsLoading?: boolean;
  fallbackDetails?: HistoryTableDetailRow[] | null;
  showSearch?: boolean;
}

export const formatDateTimeParts = (
  value: string | null,
): { date: string; time: string } => {
  if (!value) return { date: "--", time: "" };
  const date = new Date(value);

  if (isNaN(date.getTime())) return { date: value, time: "" };

  const datePart = `${date.getDate().toString().padStart(2, "0")}-${(
    date.getMonth() + 1
  )
    .toString()
    .padStart(2, "0")}-${date.getFullYear()}`;

  const timePart = date.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });

  return { date: datePart, time: timePart };
};

export const getStatusBadge = (status: string) => {
  const casedStatus = (status || "").toLowerCase();
  const variant =
    casedStatus === "success"
      ? "success"
      : casedStatus === "failed"
        ? "failed"
        : casedStatus === "pending"
          ? "pending"
          : "default";

  return (
    <span className={`history-status history-status--${variant}`}>
      <span className="history-status__dot" />
      {status}
    </span>
  );
};

const ConfigurationHistoryTable: FC<ConfigurationHistoryTableProps> = ({
  isOpen,
  handleClose,
  assetCode,
  title,
  subtitle,
  retainedText,
  actionColumnTitle,
  actionTitleField,
  issueTimeField,
  acknowledgeTimeField,
  defaultSortBy = "SentOn",
  queryKey,
  getHistory,
  getHistoryDetail,
  refetchTrigger,
  fallbackData = [],
  fallbackIsLoading = false,
  fallbackDetails = null,
  showSearch = true,
}) => {
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [searchText, setSearchText] = useState<string>("");
  const [debouncedSearchText, setDebouncedSearchText] = useState<string>("");
  const [selectedStatus, setSelectedStatus] = useState<StatusType>("All");
  const [sortBy, setSortBy] = useState<string>(defaultSortBy);
  const [sortDirection, setSortDirection] = useState<"ASC" | "DESC">("DESC");
  const [isHistoryDetailsModalOpen, setIsHistoryDetailsModalOpen] =
    useState<boolean>(false);
  const [isLoadingHistoryDetails, setIsLoadingHistoryDetails] =
    useState<boolean>(false);
  const [historyDetailsData, setHistoryDetailsData] = useState<
    HistoryTableDetailRow[] | null
  >(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchText(searchText);
    }, 1000);
    return () => clearTimeout(timer);
  }, [searchText]);

  const {
    data,
    isLoading,
    isFetching,
    refetch: refetchHistory,
  } = useQuery({
    queryKey: [
      queryKey,
      assetCode,
      pageNumber,
      debouncedSearchText,
      selectedStatus,
      sortBy,
      sortDirection,
    ],
    queryFn: () =>
      getHistory({
        assetCode: assetCode || "",
        pageNumber,
        searchText: debouncedSearchText || undefined,
        sortBy,
        sortDirection,
        pageSize: PAGE_SIZE,
        ackstatus: selectedStatus,
      }),
    enabled: isOpen && !!assetCode,
    retry: false,
  });

  useEffect(() => {
    if (isOpen && assetCode) {
      setPageNumber(1);
      setSearchText("");
      setDebouncedSearchText("");
      setSelectedStatus("All");
      setSortBy(defaultSortBy);
      setSortDirection("DESC");
    }
  }, [isOpen, assetCode, defaultSortBy]);

  useEffect(() => {
    if (isOpen && refetchTrigger && refetchTrigger !== "idle" && assetCode) {
      refetchHistory();
    }
  }, [refetchTrigger, isOpen, assetCode, refetchHistory]);

  const handleSearch = (e: React.FormEvent) => {
    if (e) e.preventDefault();
    setPageNumber(1);
  };

  const handleStatusChange = (selectedOption: any) => {
    const value = selectedOption?.value || "All";
    setSelectedStatus(value as StatusType);
    setPageNumber(1);
  };

  const handleChangeSort = (field: string, direction: string) => {
    setSortBy(field);
    setSortDirection(direction.toUpperCase() as "ASC" | "DESC");
  };

  const handleChangePage = (page: number) => {
    setPageNumber(page);
  };

  const onClickViewHistoryDetails = async (historyId: number) => {
    if (!historyId) return;

    setIsHistoryDetailsModalOpen(true);
    setIsLoadingHistoryDetails(true);
    setHistoryDetailsData(null);

    try {
      const response = await getHistoryDetail(historyId, undefined);

      setHistoryDetailsData(response?.data || fallbackDetails);
      setIsLoadingHistoryDetails(false);
    } catch (error) {
      console.log("error in get history details api call: ", error);
      // Fall back to dummy details while the API is unavailable.
      setHistoryDetailsData(fallbackDetails);
      setIsHistoryDetailsModalOpen(!!fallbackDetails?.length);
      setIsLoadingHistoryDetails(false);
    }
  };

  const handleCloseHistoryDetailsModal = () => {
    setIsHistoryDetailsModalOpen(false);
  };

  const statusOptions = [
    { value: "All", label: "All Status" },
    { value: "Success", label: "Success" },
    { value: "Failed", label: "Failed" },
    { value: "Pending", label: "Pending" },
  ];

  const columns = [
    {
      title: actionColumnTitle,
      label: "ParametersModified",
      renderProps: (row: any) => {
        const isDisabled = row?.parametersModified === 0;
        return (
          <div
            className={`history-action ${isDisabled ? "" : "cursor-pointer"}`}
            onClick={isDisabled ? undefined : () => onClickViewHistoryDetails(row?.id)}
            style={isDisabled ? { opacity: 0.5, cursor: "not-allowed" } : undefined}
          >
            <span className="history-action__title">
              {row?.[actionTitleField]}
            </span>
            <span className="history-action__count">
              {row.parametersModified} parameters modified
            </span>
          </div>
        );
      },
    },
    {
      title: "Issue Time",
      label: issueTimeField,
      renderProps: (row: any) => {
        const { date, time } = formatDateTimeParts(row?.[issueTimeField]);
        return (
          <div className="history-datetime">
            <span className="history-datetime__date">{date}</span>
            {time && <span className="history-datetime__time">{time}</span>}
          </div>
        );
      },
      isSorted: true,
    },
    {
      title: "Acknowledged Time",
      label: acknowledgeTimeField,
      renderProps: (row: any) => {
        const { date, time } = formatDateTimeParts(
          row?.[acknowledgeTimeField],
        );
        return (
          <div className="history-datetime">
            <span className="history-datetime__date">{date}</span>
            {time && <span className="history-datetime__time">{time}</span>}
          </div>
        );
      },
    },
    {
      title: "Status",
      label: "status",
      renderProps: (row: any) => getStatusBadge(row.status),
    },
    {
      title: "Initiated By",
      label: "initiatedBy",
      renderProps: (row: any) => row.initiatedBy || "--",
    },
  ];

  const historyData = data?.data || fallbackData || [];
  const totalRecords = data?.numberOfRecords || historyData.length;

  return (
    <>
      <ModalComponent
        showModal={isOpen}
        handleClose={handleClose}
        headerTitle={
          <div className="history-header">
            <div className="history-header__left">
              <span className="history-header__icon">
                <FontAwesomeIcon icon={faClockRotateLeft} />
              </span>
              <div>
                <h3 className="history-header__title">{title}</h3>
                {assetCode && (
                  <p className="history-header__subtitle">
                    {assetCode} : {subtitle}
                  </p>
                )}
              </div>
            </div>
          </div>
        }
        size="xl"
      >
        <div className="history-container">
          <div className="history-toolbar">
            {showSearch && (
              <div className="history-toolbar__left">
                <B2Form onSubmit={handleSearch}>
                  <div className="history-search-wrap">
                    <B2InputGroup className="history-search">
                      <B2InputGroup.Text>
                        <FontAwesomeIcon icon={faSearch} />
                      </B2InputGroup.Text>
                      <FormControl
                        placeholder="Search actions..."
                        value={searchText}
                        onChange={(e: any) => setSearchText(e.target.value)}
                      />
                    </B2InputGroup>
                  </div>
                </B2Form>
              </div>
            )}
            <div className="d-flex gap-2">
              <div className="history-filters">
                <B2Select
                  optionList={statusOptions}
                  value={selectedStatus}
                  onChange={(selectedOption: any) =>
                    handleStatusChange(selectedOption)
                  }
                  placeholder="All Status"
                  style={{ backgroundColor: "#f1f5f9" }}
                />
              </div>
              <div className="history-retained">
                <FontAwesomeIcon
                  icon={faClock}
                  className="history-retained__icon"
                />
                <span>{retainedText}</span>
              </div>
            </div>
          </div>

          <div className="history-table-wrapper">
            <NewTableComponent
              tableHeaders={columns}
              data={historyData}
              pageSize={PAGE_SIZE}
              current={pageNumber}
              totalNumberOfRecords={totalRecords}
              onChangePage={handleChangePage}
              onChangeSort={handleChangeSort}
              isLoadingData={isLoading || isFetching || fallbackIsLoading}
              emptyConfig={{
                title: "No Records Found",
                description:
                  "There are no configuration history entries matching your criteria.",
              }}
              wrapperHeight="400px"
              count={6}
            />
          </div>
        </div>
      </ModalComponent>

      {isHistoryDetailsModalOpen && (
        <ModalComponent
          showModal={isHistoryDetailsModalOpen}
          handleClose={handleCloseHistoryDetailsModal}
          headerTitle={
            <div className="history-header">
              <div className="history-header__left align-items-center">
                <div className="history-header__icon">
                  <FontAwesomeIcon icon={faCheckCircle} />
                </div>
                <div>
                  <h3 className="history-header__title">
                    Configuration History Details
                  </h3>
                </div>
              </div>
            </div>
          }
        >
          <div className="gateway-config">
            {isLoadingHistoryDetails && (
              <SkeletonComponent
                wrapperHeight="320px"
                skeletonHeight="44px"
                isLoading={true}
                count={6}
              >
                <span />
              </SkeletonComponent>
            )}

            {!isLoadingHistoryDetails && historyDetailsData && (
              <GetConfigTable
                columns={[
                  { key: "parameterName", title: "Name" },
                  {
                    key: "previousValue",
                    title: "Previous Value",
                    className: "gateway-config__value--current",
                  },
                  {
                    key: "currentValue",
                    title: "Current Value",
                    className: "gateway-config__value--baseline",
                  },
                ]}
                data={historyDetailsData}
                getRowKey={(row) => row.id}
                emptyMessage="No parameters returned from the device."
              />
            )}
          </div>
        </ModalComponent>
      )}
    </>
  );
};

export default ConfigurationHistoryTable;
